import type { AuthUser, TokenPayload } from '~/types/auth'

interface LoginResponse {
  token: string
}

export const authService = {
  async login(username: string, password: string, rememberMe: boolean = false) {
    const config = useRuntimeConfig()
    const { data, error } = await useFetch<string>('/api/v1/login', {
      baseURL: config.public.apiBase,
      method: 'POST',
      body: { username, password, rememberMe }
    })
    
    if (error.value) {
      throw new Error('Login failed')
    }
    
    if (!data.value) {
      throw new Error('Invalid credentials')
    }
    this.testToken(data.value)
    return data.value
  },

  async logout() {
    const config = useRuntimeConfig()
    const { error } = await useFetch('/api/v1/logout', {
      baseURL: config.public.apiBase,
      method: 'POST'
    })
    
    if (error.value) {
      throw new Error('Logout failed')
    }
  },

  // Submits an OTP for a JWT that was issued with `status: "otp_required"`.
  // The backend's `checkJwtForOtp` middleware rotates the token on success and
  // returns the new (status-less) JWT in the `token` response header.
  async verifyOtp(token: string, otp: string): Promise<{ token: string; result: unknown }> {
    const config = useRuntimeConfig()
    const response = await $fetch.raw('/api/v1/verify_otp', {
      baseURL: config.public.apiBase,
      method: 'POST',
      headers: {
        'auth': token
      },
      body: { otp }
    })

    const newToken = response.headers.get('token')
    if (!newToken) {
      throw new Error('OTP verification did not return a refreshed token')
    }

    return {
      token: newToken,
      result: response._data
    }
  },

  async verifyToken(token: string) {
    const config = useRuntimeConfig()
    const { data, error } = await useFetch('/api/v1/verify_token', {
      baseURL: config.public.apiBase,
      headers: {
        'auth': token
      }
    })
    
    if (error.value) {
      throw new Error('Token verification failed')
    }
    
    return data.value
  },

  parseToken(token: string): TokenPayload {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))

    return JSON.parse(jsonPayload)
  },

  async refreshToken(oldToken: string): Promise<string> {
    const config = useRuntimeConfig()
    const { data, error } = await useFetch<string>('/api/v1/refresh', {
      baseURL: config.public.apiBase,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${oldToken}`
      }
    })

    if (error.value) {
      throw new Error('Token refresh failed')
    }

    return data.value as string
  },

  testToken(token: string) {
    try {
      const decoded = this.parseToken(token)
      console.log('🔑 Decoded Token Details:', {
        userId: decoded.userId,
        username: decoded.username,
        role: decoded.role
      })
      
      // Let's also check when this token expires
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const { exp } = JSON.parse(atob(base64))
      if (exp) {
        const expiryDate = new Date(exp * 1000)
        console.log('⏰ Token expires:', expiryDate.toLocaleString())
      }
      
      return decoded
    } catch (error) {
      console.error('❌ Token parsing failed:', error)
      throw error
    }
  },

  async me(token: string): Promise<AuthUser> {
    const config = useRuntimeConfig()
    const response = await $fetch<{
      id: string,
      login: string,
      email: string,
      given_name: string,
      family_name: string,
      role: string,
      nickname: string,
      phone: string,
      avatar?: string
    }>('/api/v1/users/me', {
      baseURL: config.public.apiBase,
      headers: {
        'auth': token || ''
      }
    })
    console.log('🔑 Me response:', response)
    // Map API response to AuthUser interface
    return {
      userId: parseInt(response.id),
      username: response.login, // Keep email as username for backward compatibility
      email: response.email,
      name: response.given_name,
      familyName: response.family_name,
      nickname: response.nickname,
      phone: response.phone,
      role: response.role
    }
  }
} 